import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ScrollToTop } from '@/components/common/ScrollToTop';
import { useCanonical } from '@/components/common/Seo';
import { ProtectedRoute } from '@/components/routing/ProtectedRoute';
import { useAuth } from '@/contexts';

// Import seed utility (exposes window.seedListings in dev)
import '@/utils/seedData';

// Import pages
import {
  HomePage,
  ArabicLandingPage,
  SearchPage,
  ListingPage,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  BookingPage,
  BookingConfirmationPage,
  PaymentPage,
  DashboardPage,
  BookingsPage,
  SavedPage,
  MessagesPage,
  MessageThreadPage,
  ProfilePage,
  NotificationsPage,
  HostDashboardPage,
  BecomeHostPage,
  ListingsPage,
  NewListingPage,
  EditListingPage,
  CalendarPage,
  HostBookingsPage,
  BookingDetailPage,
  HostMessagesPage,
  EarningsPage,
  HostSettingsPage,
  AdminDashboard,
  UsersPage,
  UserDetailPage,
  LeadsPage,
  AdminListingsPage,
  AdminBookingsPage,
  AdminBookingDetailPage,
  AdminListingDetailPage,
  PayoutsPage,
  RefundsPage,
  ReviewsPage,
  AnnouncementsPage,
  SupportInboxPage,
  PromosPage,
  ChatPhrasesPage,
  PlatformSettingsPage,
  ContentPage,
  ReportsPage,
  AuditLogPage,
  HostTermsPage,
  HostGuidelinesPage,
  TermsPage,
  PrivacyPage,
  HelpPage,
  ContactPage,
  SafetyPage,
  CancellationPage,
  HostResourcesPage,
  CommunityPage,
  ResponsibleHostingPage,
  LandingPage,
  BlogPage,
} from '@/pages';

// Layout components
import { Header, Footer } from '@/components/layout';

// 404 page
const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
      <p className="text-xl text-gray-600 mb-8">Page not found</p>
      <a href="/" className="text-primary-600 hover:text-primary-700 font-medium">
        Go back home
      </a>
    </div>
  </div>
);

// Main layout wrapper
const MainLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, logout } = useAuth();
  return (
    <div className="min-h-screen flex flex-col">
      <Header
        user={user ? { displayName: user.displayName, photoURL: user.photoURL, role: user.role } : null}
        isAuthenticated={isAuthenticated}
        onLogout={logout}
      />
      <main className="flex-1 pt-16 lg:pt-20">{children}</main>
      <Footer />
    </div>
  );
};

/**
 * Redirects the un-routed /dashboard/bookings/:bookingId to the guest's
 * booking view. Keeps existing links and notification deep-links working
 * instead of dropping them on the 404 catch-all.
 */
const GuestBookingRedirect: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  return <Navigate to={`/booking/confirmation/${bookingId}`} replace />;
};

function App() {
  useCanonical();
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Home page - uses its own PublicLayout with transparent header */}
        <Route path="/" element={<HomePage />} />
      {/* Arabic (RTL) SEO landing */}
      <Route path="/ar" element={<ArabicLandingPage />} />
      {/* Search page - uses its own PublicLayout */}
      <Route path="/search" element={<SearchPage />} />
      {/* Listing page - uses its own PublicLayout */}
      <Route path="/listing/:id" element={<ListingPage />} />

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Booking routes - protected */}
      <Route path="/booking/:listingId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
      <Route path="/booking/confirmation/:bookingId" element={<ProtectedRoute><BookingConfirmationPage /></ProtectedRoute>} />
      <Route path="/booking/:bookingId/pay" element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />

      {/* Dashboard routes (guest) - protected */}
      <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard/bookings" element={<ProtectedRoute><MainLayout><BookingsPage /></MainLayout></ProtectedRoute>} />
      {/* Booking cards and booking notifications link here, but no such page
          was ever routed, so every one of them 404'd. The guest's booking view
          is /booking/confirmation/:bookingId — send them there. */}
      <Route path="/dashboard/bookings/:bookingId" element={<GuestBookingRedirect />} />
      <Route path="/dashboard/saved" element={<ProtectedRoute><MainLayout><SavedPage /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard/messages" element={<ProtectedRoute><MainLayout><MessagesPage /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard/messages/:threadId" element={<ProtectedRoute><MainLayout><MessageThreadPage /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard/profile" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard/notifications" element={<ProtectedRoute><MainLayout><NotificationsPage /></MainLayout></ProtectedRoute>} />

      {/* Host routes */}
      <Route path="/become-host" element={<BecomeHostPage />} />
      <Route path="/host" element={<HostDashboardPage />} />
      <Route path="/host/listings" element={<ListingsPage />} />
      <Route path="/host/listings/new" element={<NewListingPage />} />
      <Route path="/host/listings/:listingId/edit" element={<EditListingPage />} />
      <Route path="/host/calendar" element={<CalendarPage />} />
      <Route path="/host/bookings" element={<HostBookingsPage />} />
      <Route path="/host/bookings/:bookingId" element={<BookingDetailPage />} />
      <Route path="/host/messages" element={<HostMessagesPage />} />
      <Route path="/host/messages/:threadId" element={<HostMessagesPage />} />
      <Route path="/host/earnings" element={<EarningsPage />} />
      <Route path="/host/settings" element={<HostSettingsPage />} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/users" element={<UsersPage />} />
      <Route path="/admin/users/:userId" element={<UserDetailPage />} />
      <Route path="/admin/leads" element={<LeadsPage />} />
      <Route path="/admin/listings" element={<AdminListingsPage />} />
      <Route path="/admin/listings/:listingId" element={<AdminListingDetailPage />} />
      <Route path="/admin/bookings" element={<AdminBookingsPage />} />
      <Route path="/admin/bookings/:bookingId" element={<AdminBookingDetailPage />} />
      <Route path="/admin/payouts" element={<PayoutsPage />} />
      <Route path="/admin/refunds" element={<RefundsPage />} />
      <Route path="/admin/reviews" element={<ReviewsPage />} />
      <Route path="/admin/announcements" element={<AnnouncementsPage />} />
      <Route path="/admin/support" element={<SupportInboxPage />} />
      <Route path="/admin/promos" element={<PromosPage />} />
      <Route path="/admin/chat-phrases" element={<ChatPhrasesPage />} />
      <Route path="/admin/settings" element={<PlatformSettingsPage />} />
      <Route path="/admin/content" element={<ContentPage />} />
      <Route path="/admin/reports" element={<ReportsPage />} />
      <Route path="/admin/reports/audit-log" element={<AuditLogPage />} />

      {/* Legal pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/host-terms" element={<HostTermsPage />} />
      <Route path="/host-guidelines" element={<HostGuidelinesPage />} />

      {/* Support pages */}
      <Route path="/help" element={<HelpPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/safety" element={<SafetyPage />} />
      <Route path="/cancellation" element={<CancellationPage />} />
      <Route path="/host/resources" element={<HostResourcesPage />} />
      <Route path="/host/community" element={<CommunityPage />} />
      <Route path="/host/responsible-hosting" element={<ResponsibleHostingPage />} />

      {/* Blog */}
      <Route path="/blog" element={<BlogPage />} />

      {/* SEO landing pages */}
      <Route path="/lebanon/:city/:type" element={<LandingPage mode="cityType" />} />
      <Route path="/lebanon/:city" element={<LandingPage mode="city" />} />
      <Route path="/property/:type" element={<LandingPage mode="type" />} />

      {/* 404 */}
      <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
    </Routes>
    </>
  );
}

export default App;
